import { useEffect, useRef, useState } from 'react'
import { ChevronRightIcon, DownArrowIcon } from '~/components/icons'
import classNames from 'classnames'
import { fadeInFadeOutTw } from '~/styles/mixins'
import { Link } from '@remix-run/react'
import { Button } from '~/components/general/Button'
import { NavItem } from '../DesktopHeader'
import { TypeNavigationLink } from '~/types/contentful'

export type ExplorePlansProps = {
  variant?: string
  isActive: boolean
  setDropdown: (value: boolean) => void
  userAuthorized?: boolean
  menuData?: NavItem
  checkIfDarkBg?: boolean
}

export const ExplorePlans = ({
  isActive,
  setDropdown,
  userAuthorized,
  menuData,
  checkIfDarkBg,
}: ExplorePlansProps) => {
  const [activeTab] = useState<number>(0)
  const wrapperRef = useRef<HTMLUListElement>(null)
  const buttonRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false)

    return () => {
      document.removeEventListener('click', handleClickOutside, false)
    }
  }, [])

  const handleMenuToggle = () => {
    setDropdown(!isActive)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      event.target === buttonRef.current ||
      buttonRef?.current?.contains(event.target as HTMLDivElement)
    )
      return
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as HTMLDivElement)) {
      setDropdown(false)
    }
  }

  const getClassList = ({ service }: { service: NavItem }) => {
    return [
      service.label?.toLowerCase() === 'soon' ? 'locked' : '',
      service.label?.toLowerCase() === 'new' ? 'isNew' : '',
      service.color ? 'service-colored' : '',
    ].join(' ')
  }

  return (
    <>
      <div className="relative inline-block" onClick={handleMenuToggle} ref={buttonRef}>
        <Button
          text={menuData?.title}
          iconRight={
            <DownArrowIcon
              className={classNames('transition-transform duration-200', isActive && 'rotate-180')}
            />
          }
          color={'transparent'}
          className={classNames(
            'w-fit !text-mobile-s',
            checkIfDarkBg ? '!text-white hover:!text-neutral-100' : '!text-neutral-100',
          )}
        />
      </div>

      <ul
        ref={wrapperRef}
        style={{ borderRadius: '0px 0px 42px 42px' }}
        className={classNames(
          `pt-[calc(84px_+_20px)] shadow-header -translate-x-2/4 pb-8 max-md:left-2 max-md:w-[200] max-md:bg-neutral-100 hover:max-md:text-white -z-1 absolute top-0 w-full left-1/2 flex m-0 justify-around px-6 bg-neutral-5 overflow-hidden ${fadeInFadeOutTw(
            isActive,
          )}`,
        )}
      >
        <div className="grid items-start gap-8 w-full grid-cols-[0.5fr_1fr] max-w-[1420px]">
          <div className="flex flex-col justify-between h-full gap-14 pt-8 pl-8">
            <div className="flex flex-col gap-2 justify-between py-0 px-8">
              <div className="text-l text-neutral-90">
                {menuData?.groups?.[0]?.fields?.title as string}
              </div>
              <div className="text-xs text-neutral-60">
                {menuData?.groups?.[0]?.fields?.description as string}
              </div>
            </div>

            {/* .fields.card does not exist on the navigation group. check later
            {(
              menuData?.groups as TypeNavigationGroup<'WITHOUT_UNRESOLVABLE_LINKS', string>[]
            )[0]?.fields?.card?.map((item, index: number) => (
              <div
                className="flex gap-5 py-[32.5px] px-10 items-center rounded-3xl bg-blue-10"
                key={index}
              >
                <div className="flex flex-col max-w-54">
                  <div className="text-s text-neutral-80">{item?.fields.title}</div>
                  <div className="mt-2 text-neutral-70 text-xs">{item?.fields.description}</div>
                  <Button
                    text={item.fields.buttonName}
                    type={'link'}
                    target={'_self'}
                    color={'white'}
                    href={item?.fields.buttonLink}
                    iconLeft={<CrownIcon className="h-4 w-4" />}
                    className={`w-fit !h-[52px] !text-blue-50 !text-xs mt-4`}
                    onClick={() => {
                      trackEvent('click_website_menu', {
                        website_host: 'career.io',
                        click_menu_services: 'banner',
                        cta: 'Learn More',
                        target: item?.fields.ctaButtonLink,
                      })
                    }}
                  />
                </div>
                <div className="w-32 h-32">
                  <img className="w-full" src={item.fields.background.fields.file?.url} alt="" />
                </div>
              </div>
            ))} */}
          </div>
          <div className="main-menu grid-cols-[0.956fr_1fr] bg-white rounded-[20px] max-h-[600px] overflow-y-auto p-8 flex flex-col flex-wrap gap-y-2 gap-x-8 overscroll-none">
            {(
              menuData?.groups?.[0]?.fields?.links as TypeNavigationLink<
                'WITHOUT_UNRESOLVABLE_LINKS',
                string
              >[]
            )
              .map((item) => {
                return {
                  ...item?.fields,
                }
              })
              .map((plan, id: number) => (
                <div
                  className="group pt-3 pr-4 pb-4 pl-6 text-s list-none rounded-[10px] hover:bg-neutral-10"
                  key={`${activeTab}_${id}`}
                >
                  {plan?.isAuthRequired && !userAuthorized ? (
                    <a
                      className={` [&.isNew]:bg-neutral-5 [&.isNew]:[&.service-status]:bg-white [&.isNew]:[&.service-status]:text-neutral-50 flex h-full cursor-pointer transition-colors duration-200 max-md:block max-md:h-11 max-md:w-[300px] max-md:pt-2 max-md:pl-4 ${getClassList(
                        { service: plan },
                      )}`}
                      href="/app/auth/sign-in"
                      onClick={() => setDropdown(false)}
                    >
                      <div className="flex w-full rounded-lg justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs mt-1 flex gap-1">
                            <span className="text-neutral-100 ">{plan.title}</span>
                          </div>

                          <div className="text-xs max-w-[310px] mt-1 text-neutral-40 max-xl:w-50 service-description description">
                            {plan.description}
                          </div>
                        </div>

                        <ChevronRightIcon className="group-hover:visible group-hover:text-neutral-40 hidden text-white" />
                      </div>
                    </a>
                  ) : plan?.isAuthRequired && userAuthorized ? (
                    <a
                      href={plan.link}
                      className={`group [&.isNew]:bg-neutral-5 [&.isNew]:[&.service-status]:bg-white [&.isNew]:[&.service-status]:text-neutral-50 flex h-full cursor-pointer transition-colors duration-200 max-md:block max-md:h-11 max-md:w-[300px] max-md:pt-2 max-md:pl-4 ${getClassList(
                        { service: plan },
                      )}`}
                      onClick={() => setDropdown(false)}
                    >
                      <div className="flex w-full rounded-lg justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs mt-1 flex gap-1">
                            <span className="text-neutral-100">{plan.title}</span>
                          </div>
                          <div className="text-xs max-w-[310px] mt-1 text-neutral-40 max-xl:w-50 description">
                            {plan.description}
                          </div>
                        </div>
                        <ChevronRightIcon className="hidden text-white group-hover:visible group-hover:text-neutral-40" />
                      </div>
                    </a>
                  ) : (
                    <Link
                      className={`[&.isNew]:bg-neutral-5 [&.isNew]:[&.service-status]:bg-white [&.isNew]:[&.service-status]:text-neutral-50 flex h-full cursor-pointer transition-colors duration-200 max-md:block max-md:h-11 max-md:w-[300px] max-md:pt-2 max-md:pl-4 ${getClassList(
                        { service: plan },
                      )}`}
                      to={plan.link}
                      reloadDocument={plan?.link ? plan.link.includes('/mystory') : false}
                      onClick={() => setDropdown(false)}
                    >
                      <div className="group flex w-full rounded-lg justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs mt-1 flex gap-1">
                            <span className="text-neutral-100">{plan.title}</span>
                          </div>
                          <div className="text-xs max-w-[310px] mt-1 text-neutral-40 max-xl:w-50 description">
                            {plan.description}
                          </div>
                        </div>
                        <ChevronRightIcon className="group-hover:visible group-hover:text-neutral-40 invisible text-white" />
                      </div>
                    </Link>
                  )}
                </div>
              ))}
          </div>
        </div>
      </ul>
    </>
  )
}
